import {Injectable} from '@angular/core';

import {APrJetonContexte, ChargeUtileJetonContexte} from './DonneesJetonContexte';
import {ValAppSettingsService} from '../../../val-ammon-services/val-app-settings-lib/src/public-api';
import {ValApiResultat, ValBaseService} from '../../../val-ammon-services/val-services-communs-lib/src/public-api';

//Nom de l’en-tête portant le jeton de contexte utilisateur
export const EnTeteContexteUtilisateur = "CONTEXTE_UTILISATEUR";

//Clé de stockage du jeton
const CleJeton = "jeton_contexte";

//Délai au-delà duquel la connexion se poursuit sans attendre le jeton, en millisecondes
const DelaiAttenteMax = 5000;

//Taille maximale du jeton transmis en en-tête, en caractères
//  Au-delà, le jeton n’accompagne plus les appels : les APIs retombent sur la lecture directe,
//  ce pour quoi le repli existe. Borner ici évite qu’un jeton devenu trop gros fasse échouer la
//  requête elle-même sur la limite d’en-tête du reverse proxy — une panne sèche, là où le repli
//  n’est qu’une dégradation. Cf. ADR-0016, « Négatives et risques acceptés ».
//  Le jeton étant en base64url, donc ASCII, un caractère vaut un octet : le seuil se compare
//  bien à une limite exprimée en octets, sans conversion.
const TailleMaxEnTete = 30000;

//Clé de stockage de la date d’expiration du jeton
const CleExpiration = "jeton_contexte_expiration";

//Décode un segment de JWT encodé en base64 url
//  Paramètres
//    segment : Segment à décoder
//  Retourne
//    Le texte décodé
//  Trois écarts avec la base64 ordinaire, et un atob nu échoue sur les trois : « - » et « _ »
//  remplacent « + » et « / », le remplissage « = » est omis, et le contenu est de l’UTF-8 —
//  atob rend une chaîne d’octets, dont les accents seraient faux sans TextDecoder.
function DecoderBase64Url (segment: string) : string
{
  let base64: string;
  let remplissage: number;
  let binaire: string;

  base64 = segment.replace (/-/g, "+").replace (/_/g, "/");
  remplissage = (4 - (base64.length % 4)) % 4;
  binaire = atob (base64 + "=".repeat (remplissage));

  return new TextDecoder ("utf-8").decode (Uint8Array.from (binaire, caractere => caractere.charCodeAt (0)));
}

//Service du jeton de contexte utilisateur
//  Le jeton est demandé une fois par connexion et conservé pour la session. Il n’est jamais
//  renouvelé : il vit dix heures, alignées sur le SSO Session Max du realm. Un changement de
//  rattachement prend donc effet à la reconnexion, et c’est assumé (ADR-0016, décision D2).
//
//  Le stockage est le sessionStorage, celui d’angular-oauth2-oidc par défaut : le jeton de
//  contexte et le jeton Keycloak apparaissent et disparaissent donc ensemble. Un rafraîchissement
//  de page les conserve tous les deux, un nouvel onglet n’en a aucun des deux.
@Injectable (
{
  providedIn: 'root'
})
export class JetonContexteService extends ValBaseService
{
  //Indique que l’avertissement de taille excessive a déjà été émis
  //  Le getter de transmission est lu à chaque requête HTTP : sans ce drapeau, un jeton trop
  //  gros noierait la console d’une ligne par appel. Le service étant fourni à la racine,
  //  l’avertissement paraît donc une fois par chargement de page, ce qui suffit à le voir.
  private avertissementTailleEmis: boolean = false;

  //Création
  //  Paramètres
  //    appSettingsService : Service d’accès à la configuration de l’application
  constructor (private appSettingsService: ValAppSettingsService)
  {
    super ();
  }

  //Url de base
  protected override get BaseUrl () : string
  {
    return this.appSettingsService.Configuration.UrlApi.Permissions;
  }

  //Jeton de contexte de la session, nul s’il n’y en a pas ou s’il est périmé
  public get Jeton () : string | null
  {
    let jeton: string | null;
    let expiration: string | null;

    jeton = sessionStorage.getItem (CleJeton);
    expiration = sessionStorage.getItem (CleExpiration);

    if (!jeton || !expiration)
      return null;

    //Un jeton périmé serait de toute façon rejeté par les APIs, qui retomberaient sur la lecture
    //directe : ce contrôle ne protège rien, il évite de payer une vérification perdue par requête.
    if (Date.parse (expiration) <= Date.now ())
    {
      this.Effacer ();
      return null;
    }

    return jeton;
  }

  //Jeton à transmettre en en-tête, nul s’il n’y en a pas ou s’il dépasse la taille maximale
  //  Distinct de Jeton, et il doit le rester : Angular exploite la charge utile pour lui-même —
  //  les applications autorisées en viennent — et cesser de la lire fermerait des écrans que rien
  //  n’oblige à fermer. Seule la transmission est bornée, jamais la lecture.
  //  La règle vit ici plutôt que dans l’intercepteur, avec le seuil et le nom de l’en-tête : un
  //  second appelant qui transmettrait le jeton la trouverait déjà écrite.
  public get JetonTransmissible () : string | null
  {
    let jeton: string | null;

    jeton = this.Jeton;

    if (!jeton)
      return null;

    if (jeton.length > TailleMaxEnTete)
    {
      this.AvertirTailleExcessive (jeton.length);

      return null;
    }

    return jeton;
  }

  //Charge utile du jeton de la session, nulle s’il n’y a pas de jeton ou s’il est illisible
  //  La signature n’est PAS vérifiée, et c’est un choix : une vérification en JavaScript ne
  //  protégerait de rien, le code et le jeton étant tous deux dans le navigateur. La seule
  //  vérification qui compte est celle des APIs. Cf. ADR-0017, décision D8.
  public get ChargeUtile () : ChargeUtileJetonContexte | null
  {
    let jeton: string | null;
    let segments: string[];

    jeton = this.Jeton;

    if (!jeton)
      return null;

    segments = jeton.split (".");

    if (segments.length !== 3)
      return null;

    try
    {
      return JSON.parse (DecoderBase64Url (segments[1])) as ChargeUtileJetonContexte;
    }
    catch
    {
      //Un jeton illisible se traite comme un jeton absent : l’appelant retombe sur la lecture
      //directe, il ne conclut pas au refus sur une charge utile qu’il n’a pas su lire.
      console.warn ("Charge utile du jeton de contexte illisible, lecture directe.");

      return null;
    }
  }

  //Attend la demande du jeton sans jamais retarder la connexion au-delà du délai maximal
  //  Retourne
  //    Opération asynchrone
  //  Remarques
  //    ExecuterRequeteGet n’a pas de délai maximal : sans cette borne, une instance d’Ammon
  //    Permissions qui ne répond pas laisserait l’utilisateur sur la page de callback
  //    indéfiniment. La requête n’est pas annulée pour autant — si elle aboutit après coup,
  //    le jeton est conservé et servira aux appels suivants.
  public async DemanderSansBloquer () : Promise<void>
  {
    await Promise.race ([this.Demander (),
                         new Promise<void> (poursuivre => setTimeout (poursuivre, DelaiAttenteMax))]);
  }

  //Demande le jeton de contexte à Ammon Permissions et le conserve pour la session
  //  Retourne
  //    Opération asynchrone
  public async Demander () : Promise<void>
  {
    let resultat: ValApiResultat<APrJetonContexte>;

    //Effacer d’abord : une reconnexion sous une autre identité dans le même onglet ne doit pas
    //conserver le jeton du précédent. Il serait rejeté par les APIs faute d’appariement, donc
    //sans danger — mais il ferait payer une vérification perdue à chaque requête.
    this.Effacer ();

    try
    {
      resultat = await this.ExecuterRequeteGet<APrJetonContexte> ("/v1.0/user/jeton");

      if (resultat?.status === "success" && resultat.resultat)
      {
        sessionStorage.setItem (CleJeton, resultat.resultat.jeton);
        sessionStorage.setItem (CleExpiration, new Date (resultat.resultat.expiration).toISOString ());
      }
      else
        console.warn ("Jeton de contexte non obtenu, les APIs liront le contexte directement.");
    }
    catch
    {
      //L’absence de jeton n’est pas une erreur pour l’utilisateur : les APIs retombent sur la
      //lecture directe dans Ammon Permissions. Ne jamais laisser cet échec interrompre la
      //connexion — ce serait rendre toute la plateforme indisponible sur une dégradation.
      console.warn ("Jeton de contexte indisponible, les APIs liront le contexte directement.");
    }
  }

  //Efface le jeton de contexte de la session
  public Effacer () : void
  {
    sessionStorage.removeItem (CleJeton);
    sessionStorage.removeItem (CleExpiration);

    //Le drapeau suit le jeton : le prochain, s’il est trop gros à son tour, doit s’annoncer.
    this.avertissementTailleEmis = false;
  }

  //Signale une fois que le jeton n’est pas transmis faute de tenir dans l’en-tête
  //  Paramètres
  //    taille : Taille du jeton, en caractères
  private AvertirTailleExcessive (taille: number) : void
  {
    if (this.avertissementTailleEmis)
      return;

    this.avertissementTailleEmis = true;

    //Le repli est silencieux par construction : sans cette trace, un contexte lu directement
    //par les dix APIs ne se distinguerait pas d’un jeton jamais obtenu, et la cause — un jeton
    //devenu trop gros — resterait invisible jusqu’à ce qu’on aille la chercher.
    console.warn (`Jeton de contexte de ${taille} caractères, au-delà de la limite de ${TailleMaxEnTete} : `
                + "non transmis aux APIs, qui liront le contexte directement.");
  }
}
